import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Ruler, Send, MessageCircle, Store, Phone, Truck, Award } from 'lucide-react';
import { HERO_IMAGE } from '../data/catalog';
import ProductCard from '../components/ProductCard';
import { isProductVisible, useAvailabilityTick } from '../utils/availability';
import {
  useSiteConfig,
  getActiveBanners,
  getHomeCategories,
  getFeaturedProducts,
  resolveDestination,
} from '../utils/siteConfig';
import { useCatalogTick } from '../utils/customProducts';

const STEPS = [
  { icon: Search, title: 'Escolha os tecidos', text: 'Navegue pelas categorias ou utilize a busca.' },
  { icon: Ruler, title: 'Informe a metragem', text: 'Adicione os tecidos e quantidades desejadas à sua solicitação.' },
  { icon: Send, title: 'Envie sua solicitação', text: 'Informe seu WhatsApp e envie sua seleção.' },
  { icon: MessageCircle, title: 'Receba o retorno', text: 'Nossa equipe confirma disponibilidade, quantidades e valores pelo WhatsApp.' },
];

function HeroBadges() {
  return (
    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-xs text-foreground/60">
      <span className="flex items-center gap-1.5"><Award className="h-4 w-4 text-navy" /> Qualidade e tradição</span>
      <span className="flex items-center gap-1.5"><Store className="h-4 w-4 text-navy" /> Atendimento especializado</span>
      <span className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-navy" /> Há mais de 30 anos com você</span>
    </div>
  );
}

function HeroSlide({ title, text, buttonText, dest, imgDesktop, imgMobile, showBadges = false }) {
  return (
    <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 py-10 md:grid-cols-2 md:py-0">
      <div className="order-2 md:order-1 md:py-20">
        <h1 className="whitespace-pre-line font-serif text-4xl leading-[1.1] tracking-tight text-navy sm:text-5xl lg:text-6xl">
          {title}
        </h1>
        {text && (
          <p className="mt-5 max-w-md text-base leading-relaxed text-foreground/70 md:text-lg">{text}</p>
        )}
        {buttonText && (
          <Link
            to={dest}
            data-testid="hero-explore-button"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-hover"
          >
            {buttonText} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
        {showBadges && <HeroBadges />}
      </div>
      <div className="order-1 md:order-2 md:h-full">
        <img
          src={imgMobile || imgDesktop}
          alt={title}
          className="h-64 w-full rounded-2xl object-cover shadow-lg sm:h-80 md:hidden"
          data-testid="hero-image-mobile"
        />
        <img
          src={imgDesktop}
          alt={title}
          className="hidden md:block md:h-full md:w-full md:rounded-l-[3rem] md:object-cover"
          data-testid="hero-image-desktop"
        />
      </div>
    </div>
  );
}

export default function Home() {
  useAvailabilityTick();
  useCatalogTick();
  const cfg = useSiteConfig();
  const banners = getActiveBanners(cfg);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    if (banners.length < 2) return undefined;
    const t = setInterval(() => setSlide((s) => (s + 1) % banners.length), 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  useEffect(() => {
    if (slide >= banners.length) setSlide(0);
  }, [banners.length, slide]);

  const featured = getFeaturedProducts(cfg).filter((p) => isProductVisible(p));
  const cats = getHomeCategories(cfg).filter((c) => c.show);
  const store = cfg.store;
  const current = banners.length ? banners[Math.min(slide, banners.length - 1)] : null;

  return (
    <main>
      <section className="relative overflow-hidden bg-[#F4EFE8]" data-testid="hero-section">
        {current ? (
          <div key={current.id} className="animate-fade-up" data-testid="hero-banner-slide">
            <HeroSlide
              title={current.title}
              text={current.text}
              buttonText={current.buttonText}
              dest={resolveDestination(current.destType, current.destValue)}
              imgDesktop={current.imageDesktop}
              imgMobile={current.imageMobile}
            />
          </div>
        ) : (
          <HeroSlide
            title={cfg.home.heroTitle}
            text={cfg.home.heroText}
            buttonText={cfg.home.heroButtonText}
            dest={resolveDestination(cfg.home.heroDestType, cfg.home.heroDestValue)}
            imgDesktop={cfg.home.heroImageDesktop || HERO_IMAGE}
            imgMobile={cfg.home.heroImageMobile}
            showBadges
          />
        )}
        {banners.length > 1 && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center gap-2" data-testid="hero-carousel-dots">
            {banners.map((b, i) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setSlide(i)}
                aria-label={`Ir para banner ${i + 1}`}
                aria-pressed={i === slide}
                data-testid={`hero-dot-${i}`}
                className={`h-2.5 w-2.5 rounded-full transition-colors ${i === slide ? 'bg-navy' : 'bg-navy/30 hover:bg-navy/50'}`}
              />
            ))}
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-16" data-testid="categories-section">
        <div className="flex items-end justify-between">
          <h2 className="font-serif text-3xl tracking-tight text-navy">Nossas Categorias</h2>
          <Link
            to="/categorias"
            className="hidden items-center gap-1 text-sm font-semibold text-navy hover:underline sm:flex"
            data-testid="categories-view-all-link"
          >
            Ver todas as categorias <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="no-scrollbar -mx-4 mt-6 flex gap-4 overflow-x-auto px-4 pb-2 snap-x md:mx-0 md:grid md:grid-cols-3 md:overflow-visible md:px-0 lg:grid-cols-6">
          {cats.map((c) => (
            <Link
              key={c.slug}
              to={`/categoria/${c.slug}`}
              data-testid={`category-card-${c.slug}`}
              className="group relative w-44 shrink-0 snap-start overflow-hidden rounded-xl md:w-auto"
            >
              {c.image ? (
                <img
                  src={c.image}
                  alt={`Categoria ${c.name}`}
                  loading="lazy"
                  className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="flex aspect-[3/4] w-full items-center justify-center bg-navy-light" data-testid={`category-card-no-image-${c.slug}`}>
                  <span className="font-serif text-4xl text-navy/30">{c.name.charAt(0)}</span>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/80 via-navy-dark/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                <p className="font-serif text-lg leading-tight">{c.name}</p>
                <p className="mt-0.5 text-xs text-white/80">{c.tagline}</p>
              </div>
            </Link>
          ))}
        </div>
        <Link
          to="/categorias"
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline sm:hidden"
          data-testid="categories-view-all-link-mobile"
        >
          Ver todas as categorias <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      <section id="como-funciona" className="mt-16 bg-navy-light py-14" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4">
          <h2 className="font-serif text-3xl tracking-tight text-navy">Como funciona?</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <div key={s.title} className="rounded-xl bg-white/70 p-5" data-testid={`how-it-works-step-${i + 1}`}>
                <s.icon className="h-7 w-7 text-navy" strokeWidth={1.5} />
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                  Passo {i + 1}
                </p>
                <p className="mt-1 font-semibold uppercase tracking-wide text-navy">{s.title}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground/70">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-foreground/60" data-testid="how-it-works-disclaimer">
            Enviar a solicitação <strong>não representa pagamento</strong> nem confirmação automática
            de estoque. Nossa equipe confirma tudo pessoalmente pelo WhatsApp.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pt-16" data-testid="featured-section">
        <div className="flex items-end justify-between">
          <h2 className="font-serif text-3xl tracking-tight text-navy">Destaques</h2>
          <Link
            to="/categoria/todos"
            className="flex items-center gap-1 text-sm font-semibold text-navy hover:underline"
            data-testid="featured-view-all-link"
          >
            Ver todos os produtos <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 lg:grid-cols-6">
          {featured.map((p, i) => (
            <ProductCard key={p.slug} product={p} index={i} />
          ))}
        </div>
      </section>

      <section className="mx-auto mt-16 max-w-7xl px-4" data-testid="trust-section">
        <div className="grid gap-6 rounded-2xl border border-navy/10 bg-white p-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="flex items-start gap-3">
            <Store className="h-6 w-6 shrink-0 text-navy" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-semibold">Visite nossa loja física</p>
              <p className="text-xs text-muted-foreground">{store.address} · {store.cityUf}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="h-6 w-6 shrink-0 text-navy" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-semibold">Atendimento por telefone</p>
              <p className="text-xs text-muted-foreground">{store.phone}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MessageCircle className="h-6 w-6 shrink-0 text-[#128C4B]" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-semibold">Fale pelo WhatsApp</p>
              <p className="text-xs text-muted-foreground">{store.whatsapp}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Truck className="h-6 w-6 shrink-0 text-navy" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-semibold">Enviamos para todo o Brasil</p>
              <p className="text-xs text-muted-foreground">Consulte condições</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
